import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, ArrowLeft, Mail, AlertCircle, PauseCircle, PlayCircle, XCircle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Simple unsubscribe (no token — backward compat)
const SimpleUnsubscribe = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleUnsubscribe = async () => {
    if (!email) return;
    setStatus('loading');
    setErrorMsg('');
    try {
      const res = await fetch(`${API_URL}/api/cfq/unsubscribe?email=${encodeURIComponent(email)}`);
      if (res.ok) { setStatus('success'); }
      else {
        const d = await res.json().catch(() => ({}));
        setErrorMsg(d.detail || 'Something went wrong.');
        setStatus('error');
      }
    } catch {
      setErrorMsg('Network error. Please try again.');
      setStatus('error');
    }
  };

  if (status === 'success') return (
    <Card>
      <SuccessState icon={<CheckCircle className="w-7 h-7 text-emerald-500" />} title="You've been unsubscribed"
        body={<><strong className="text-[#071B3A]/70">{email}</strong> has been removed from the Cultural Fluency mailing list.</>}
        sub="Changed your mind? You can re-subscribe at any time from the website." />
    </Card>
  );

  return (
    <Card>
      <BrandHeader />
      {status === 'error' && <ErrorBanner msg={errorMsg} />}
      <input type="email" value={email} onChange={e => setEmail(e.target.value)}
        placeholder="your@email.com"
        className="w-full border border-[#071B3A]/10 rounded-xl px-4 py-3 text-sm text-[#071B3A] placeholder-[#071B3A]/20 focus:outline-none focus:ring-2 focus:ring-[#C99316]/20 focus:border-[#C99316]/30 mb-3"
        data-testid="unsubscribe-email-input" />
      <button onClick={handleUnsubscribe} disabled={!email || status === 'loading'}
        className="w-full bg-[#071B3A] text-[#FAF8F3] py-3 rounded-xl text-sm font-medium hover:bg-[#071B3A]/90 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
        data-testid="unsubscribe-btn">
        {status === 'loading' ? 'Processing…' : 'Unsubscribe'}
      </button>
      <Link to="/" className="block text-center text-xs text-[#071B3A]/25 hover:text-[#071B3A]/50 transition-colors mt-4">
        ← Back to Third Culture Leadership
      </Link>
    </Card>
  );
};

// ── Full preference centre (with token)
const PreferenceCentre = ({ email, token }) => {
  const [prefs, setPrefs] = useState(null);
  const [loadState, setLoadState] = useState('loading'); // loading | ready | error
  const [actionState, setActionState] = useState('idle'); // idle | loading | success | error
  const [actionResult, setActionResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/cfq/nurture/subscriber-prefs?email=${encodeURIComponent(email)}&token=${encodeURIComponent(token)}`
        );
        if (res.ok) { setPrefs(await res.json()); setLoadState('ready'); }
        else { setLoadState('error'); setErrorMsg('This link has expired or is invalid. Please contact us.'); }
      } catch {
        setLoadState('error'); setErrorMsg('Network error. Please try again.');
      }
    })();
  }, [email, token]);

  const doAction = async (action) => {
    setActionState('loading');
    setErrorMsg('');
    try {
      const res = await fetch(`${API_URL}/api/cfq/nurture/subscriber-prefs/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token, action }),
      });
      if (res.ok) {
        const d = await res.json();
        setActionResult(d);
        setActionState('success');
        // Update prefs optimistically
        if (action === 'unsubscribe') setPrefs(p => ({ ...p, status: 'unsubscribed', nurture_paused: true }));
        if (action === 'pause')       setPrefs(p => ({ ...p, nurture_paused: true }));
        if (action === 'resume')      setPrefs(p => ({ ...p, nurture_paused: false }));
      } else {
        const d = await res.json().catch(() => ({}));
        setErrorMsg(d.detail || 'Something went wrong.');
        setActionState('error');
      }
    } catch {
      setErrorMsg('Network error. Please try again.');
      setActionState('error');
    }
  };

  if (loadState === 'loading') return (
    <Card><div className="flex items-center justify-center py-10 gap-3 text-[#071B3A]/30"><Loader2 className="animate-spin" size={20} /><span className="text-sm">Loading your preferences…</span></div></Card>
  );

  if (loadState === 'error') return (
    <Card><ErrorBanner msg={errorMsg} /><Link to="/" className="block text-center text-xs text-[#071B3A]/25 hover:text-[#071B3A]/50 transition-colors mt-4">← Back to site</Link></Card>
  );

  const isUnsubscribed = prefs?.status === 'unsubscribed';
  const isPaused       = prefs?.nurture_paused && !isUnsubscribed;

  // Post-action success states
  if (actionState === 'success' && actionResult?.action === 'unsubscribed') return (
    <Card>
      <SuccessState icon={<XCircle className="w-7 h-7 text-red-400" />} title="You've been unsubscribed"
        body={<><strong className="text-[#071B3A]/70">{email}</strong> has been removed from the Cultural Fluency mailing list.</>}
        sub="Changed your mind? Reply to any of our emails or contact us directly." />
    </Card>
  );
  if (actionState === 'success' && actionResult?.action === 'paused') return (
    <Card>
      <SuccessState icon={<PauseCircle className="w-7 h-7 text-amber-500" />} title="Emails paused for 30 days"
        body="We'll give you some space. Your nurture sequence will resume automatically in 30 days — no action needed."
        sub="Come back here at any time to resume early or unsubscribe." />
    </Card>
  );
  if (actionState === 'success' && actionResult?.action === 'resumed') return (
    <Card>
      <SuccessState icon={<PlayCircle className="w-7 h-7 text-emerald-500" />} title="Emails resumed"
        body="Welcome back — your nurture sequence will continue from where you left off."
        sub="" />
    </Card>
  );

  return (
    <Card>
      <BrandHeader />
      <div className="mb-5">
        <p className="text-[#071B3A]/60 text-sm leading-relaxed">
          Manage your email preferences for <strong className="text-[#071B3A]/80">{prefs?.first_name || email}</strong>.
        </p>
        <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium"
          style={{
            background: isUnsubscribed ? '#fef2f2' : isPaused ? '#fffbeb' : '#f0fdf4',
            color:      isUnsubscribed ? '#dc2626'  : isPaused ? '#92400e'  : '#166534',
          }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'currentColor' }} />
          {isUnsubscribed ? 'Unsubscribed' : isPaused ? 'Paused' : 'Active'}
        </div>
      </div>

      {(actionState === 'error') && <ErrorBanner msg={errorMsg} />}

      {isUnsubscribed ? (
        <div className="text-center py-4">
          <p className="text-[#071B3A]/40 text-sm">You are already unsubscribed.</p>
          <p className="text-[#071B3A]/25 text-xs mt-1">To re-subscribe, visit the main website.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="pref-actions">
          {isPaused ? (
            <ActionButton
              onClick={() => doAction('resume')}
              disabled={actionState === 'loading'}
              icon={<PlayCircle size={16} />}
              label="Resume emails"
              sub="Continue where you left off"
              color="#166534" bg="#f0fdf4" border="#bbf7d0"
              testId="resume-btn"
            />
          ) : (
            <ActionButton
              onClick={() => doAction('pause')}
              disabled={actionState === 'loading'}
              icon={<PauseCircle size={16} />}
              label="Pause for 30 days"
              sub="Take a break — we'll resume automatically"
              color="#92400e" bg="#fffbeb" border="#fde68a"
              testId="pause-btn"
            />
          )}
          <ActionButton
            onClick={() => { if (window.confirm('Are you sure you want to unsubscribe? You will stop receiving all Cultural Fluency nurture emails.')) doAction('unsubscribe'); }}
            disabled={actionState === 'loading'}
            icon={<XCircle size={16} />}
            label="Unsubscribe"
            sub="Remove me from all Cultural Fluency emails"
            color="#dc2626" bg="#fef2f2" border="#fecaca"
            testId="full-unsubscribe-btn"
          />
        </div>
      )}

      <Link to="/" className="block text-center text-xs text-[#071B3A]/25 hover:text-[#071B3A]/50 transition-colors mt-6">
        ← Back to Third Culture Leadership
      </Link>
    </Card>
  );
};

// ── Shared small components
const Card = ({ children }) => (
  <div className="bg-white rounded-2xl p-8 shadow-sm border border-black/5">{children}</div>
);

const BrandHeader = () => (
  <div className="text-center mb-6">
    <div className="inline-flex items-center justify-center w-12 h-12 bg-[#071B3A]/5 rounded-full mb-3">
      <Mail className="w-5 h-5 text-[#071B3A]/50" />
    </div>
    <h1 className="font-serif text-2xl text-[#071B3A]">Email Preferences</h1>
    <p className="text-[#071B3A]/40 text-sm mt-1 leading-relaxed">
      Cultural Fluency mailing list
    </p>
  </div>
);

const SuccessState = ({ icon, title, body, sub }) => (
  <div className="text-center space-y-4">
    <div className="inline-flex items-center justify-center w-14 h-14 bg-gray-50 rounded-full">{icon}</div>
    <h1 className="font-serif text-2xl text-[#071B3A]">{title}</h1>
    <p className="text-[#071B3A]/50 text-sm leading-relaxed">{body}</p>
    {sub && <p className="text-[#071B3A]/30 text-xs leading-relaxed">{sub}</p>}
    <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#C99316] hover:text-[#C99316]/70 transition-colors mt-2">
      <ArrowLeft size={14} /> Back to site
    </Link>
  </div>
);

const ErrorBanner = ({ msg }) => (
  <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-100 rounded-xl mb-4">
    <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
    <p className="text-red-600 text-sm">{msg}</p>
  </div>
);

const ActionButton = ({ onClick, disabled, icon, label, sub, color, bg, border, testId }) => (
  <button onClick={onClick} disabled={disabled}
    className="w-full text-left p-4 rounded-xl border transition-all disabled:opacity-50 hover:shadow-sm"
    style={{ background: bg, borderColor: border, color }}
    data-testid={testId}>
    <div className="flex items-center gap-3">
      <span className="flex-shrink-0" style={{ color }}>{icon}</span>
      <div>
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs mt-0.5" style={{ color, opacity: 0.65 }}>{sub}</p>
      </div>
      {disabled && <Loader2 size={14} className="ml-auto animate-spin" style={{ color }} />}
    </div>
  </button>
);

// ── Root page
const Unsubscribe = () => {
  const [searchParams] = useSearchParams();
  const email  = searchParams.get('email') || '';
  const token  = searchParams.get('token') || '';

  // Auto-unsubscribe for old-style direct API links (no token, but email pre-filled)
  const [autoUnsubStatus, setAutoUnsubStatus] = useState(null);
  const didAutoRef = useRef(false);

  useEffect(() => {
    // Old links go directly to the API — only if NO token provided
    if (email && !token && !didAutoRef.current) {
      didAutoRef.current = true;
      (async () => {
        try {
          const res = await fetch(`${API_URL}/api/cfq/unsubscribe?email=${encodeURIComponent(email)}`);
          setAutoUnsubStatus(res.ok ? 'success' : 'error');
        } catch {
          setAutoUnsubStatus('error');
        }
      })();
    }
  }, [email, token]);

  return (
    <div
      className="min-h-screen bg-[#FAF8F3] flex flex-col items-center justify-center px-6 py-16"
      data-testid="unsubscribe-page"
    >
      <div className="max-w-md w-full">
        {/* Brand mark */}
        <div className="text-center mb-10">
          <p className="font-serif text-[#071B3A] text-lg tracking-wider">Third Culture Leadership</p>
          <p className="text-[#071B3A]/40 text-xs mt-1 tracking-widest uppercase">Cultural Fluency Mailing List</p>
        </div>

        {/* Route to the correct component */}
        {email && token ? (
          <PreferenceCentre email={email} token={token} />
        ) : autoUnsubStatus === 'success' ? (
          <Card>
            <SuccessState icon={<CheckCircle className="w-7 h-7 text-emerald-500" />}
              title="You've been unsubscribed"
              body={<><strong className="text-[#071B3A]/70">{email}</strong> has been removed from the Cultural Fluency mailing list.</>}
              sub="Changed your mind? You can re-subscribe at any time from the website." />
          </Card>
        ) : autoUnsubStatus === 'error' ? (
          <Card><ErrorBanner msg="Something went wrong. Please try again or contact us." /><Link to="/" className="block text-center text-xs text-[#071B3A]/25 mt-4">← Back to site</Link></Card>
        ) : !token && !email ? (
          <SimpleUnsubscribe />
        ) : null}

        {/* CAN-SPAM footer */}
        <p className="text-center text-[#071B3A]/20 text-[11px] mt-8 leading-relaxed">
          Third Culture Leadership · United Kingdom<br />
          You received this email because you subscribed to the Cultural Fluency mailing list.<br />
          © {new Date().getFullYear()} Third Culture Leadership. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default Unsubscribe;
